"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CatalogBootstrap = exports.CatalogBootstrapCategory = exports.CatalogBootstrapItem = exports.CatalogItemDiscofferRule = exports.CatalogItemImage = exports.CatalogBootstrapBarcode = exports.I18nBootstrapBundle = exports.HeartbeatRequest = exports.ActivationResponse = exports.PinPepperDeliveryResponse = exports.VersionedPinPepperMaterial = exports.ActivationRequest = exports.ActivationCodePattern = exports.TerminalSummary = exports.TerminalProfileSummary = exports.ApplicationVariant = exports.LocaleCode = exports.TerminalStatus = exports.I18N_SURFACES = exports.TERMINAL_I18N_SURFACES = exports.TerminalKind = exports.TillKind = void 0;
const zod_1 = require("zod");
const country_pack_js_1 = require("./country-pack.js");
const tax_rates_js_1 = require("./tax-rates.js");
const receipt_config_js_1 = require("./receipt-config.js");
const catalog_hotkeys_js_1 = require("./catalog-hotkeys.js");
const catalog_js_1 = require("./catalog.js");
const terminal_params_js_1 = require("./terminal-params.js");
/**
 * Canonical terminal-management contracts. Used by BOS REST DTOs and
 * by the Flutter terminal app's HTTP client (via `quicktype` or hand
 * port to Dart in Sprint 2's terminals work).
 */
/**
 * The kinds a till can be. Everything that exists because a terminal SELLS —
 * provisioning compositions, the register directory, screen flows — keys on
 * this, not on {@link TerminalKind}, so adding a non-till kind does not
 * silently widen them.
 */
exports.TillKind = zod_1.z.enum(['pos', 'sco', 'attendant']);
/**
 * Every kind of device activated through Terminal Management. `link` (RL-449)
 * is re:form.lite.link, the in-store connector host: it pairs with an
 * activation code and holds a terminal token like a till, but it is not a till
 * and is refused on till routes (ADR-0014 §2).
 */
exports.TerminalKind = zod_1.z.enum([...exports.TillKind.options, 'link']);
/**
 * Single source of truth for i18n surfaces (H-42). The terminal-facing
 * surfaces are the set delivered to terminals at bootstrap and via
 * `/sync/i18n`; `admin_ui` is the BOS-only surface that terminals never
 * receive. Previously these were hand-rolled in three+ places
 * (bootstrap.controller, i18n.service, the sync i18n enum, the spec).
 */
exports.TERMINAL_I18N_SURFACES = [
    'pos',
    'sco',
    'attendant',
    'shared',
    'receipt_label',
];
exports.I18N_SURFACES = ['admin_ui', ...exports.TERMINAL_I18N_SURFACES];
exports.TerminalStatus = zod_1.z.enum(['provisioned', 'active', 'revoked']);
exports.LocaleCode = zod_1.z.string().regex(/^[a-z]{2,3}$/, 'lowercase ISO-639 alpha-2 or alpha-3');
exports.ApplicationVariant = zod_1.z.enum(['food_self_order', 'kitchen_station']);
exports.TerminalProfileSummary = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string().min(1).max(120),
    primaryLocale: exports.LocaleCode,
    availableLocales: zod_1.z.array(exports.LocaleCode).min(1),
    /**
     * Peripherals attached to terminals on this profile (RFL-177), e.g.
     * "scale" for a POS lane with a produce scale. Grants sync domains
     * beyond the kind defaults (a POS with "scale" also receives
     * `sync.weight-standards`). Additive — absent in pre-RFL-177 payloads.
     */
    peripherals: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * The executable this profile's terminals run when the authentication
     * kind alone does not decide it (ADR-0012 §3, FN-96): `kitchen_station`
     * under `attendant`, `food_self_order` under `sco`. Absent/null = the
     * kind's default application. Additive.
     */
    applicationVariant: exports.ApplicationVariant.nullable().optional(),
});
exports.TerminalSummary = zod_1.z.object({
    id: zod_1.z.string(),
    code: zod_1.z.string().min(1).max(64),
    displayName: zod_1.z.string().min(1).max(255),
    kind: exports.TerminalKind,
    status: exports.TerminalStatus,
    profileId: zod_1.z.string().nullable(),
    lastHeartbeatAt: zod_1.z.string().datetime().nullable(),
    lastVersion: zod_1.z.string().nullable(),
    activatedAt: zod_1.z.string().datetime().nullable(),
});
// ---------- Activation ----------
exports.ActivationCodePattern = /^RFLT-[A-Z0-9]{8}-[A-Z0-9]{4}$/;
exports.ActivationRequest = zod_1.z.object({
    activationCode: zod_1.z.string().regex(exports.ActivationCodePattern, 'malformed activation code'),
    fingerprint: zod_1.z.string().min(1).max(255).optional(),
});
/**
 * One tenant-scoped cashier PIN pepper delivered only to authenticated
 * terminal secret storage. `material` is base64-encoded UTF-8 bytes and must
 * never be logged or echoed into ordinary sync payloads.
 */
exports.VersionedPinPepperMaterial = zod_1.z.object({
    version: zod_1.z.number().int().positive(),
    material: zod_1.z.string().min(1),
});
exports.PinPepperDeliveryResponse = zod_1.z.object({
    pinPeppers: zod_1.z.array(exports.VersionedPinPepperMaterial),
});
exports.ActivationResponse = zod_1.z.object({
    /** SSC-229: authenticated tenant binding, absent only on older BOS. */
    tenantId: zod_1.z.string().min(1).optional(),
    secureScaleEnabled: zod_1.z.boolean().optional(),
    terminal: exports.TerminalSummary,
    profile: exports.TerminalProfileSummary.nullable(),
    accessToken: zod_1.z.string(),
    expiresAt: zod_1.z.string().datetime(),
    /**
     * RL-155: the terminal's role, stated at the top level and NON-NULLABLE.
     *
     * Role resolution (IMG-1) makes this load-bearing: whatever activates learns
     * the role from it and installs the corresponding package. It was previously
     * reachable only as `terminal.kind` and modelled nullable on the consumer
     * side, so a client that found nothing had no contract telling it to stop —
     * and one that guessed `pos` installs the wrong application on an SCO lane.
     *
     * Declaring it here is what lets a consumer hard-fail rather than guess.
     * `terminal.kind` is unchanged, so existing readers keep working.
     */
    terminalKind: exports.TerminalKind,
    pinPeppers: zod_1.z.array(exports.VersionedPinPepperMaterial),
});
// ---------- Heartbeat ----------
exports.HeartbeatRequest = zod_1.z.object({
    version: zod_1.z.string().min(1).max(64),
    uptimeSeconds: zod_1.z.number().int().min(0).optional(),
    memoryFreeMB: zod_1.z.number().int().min(0).optional(),
});
// ---------- i18n bootstrap ----------
/** A locale → key → string-template bundle. Strings are ICU MessageFormat (TRS §36.6). */
exports.I18nBootstrapBundle = zod_1.z.record(exports.LocaleCode, zod_1.z.record(zod_1.z.string(), zod_1.z.string()));
// ---------- Catalog bootstrap ----------
exports.CatalogBootstrapBarcode = zod_1.z.object({
    barcode: zod_1.z.string(),
    isPrimary: zod_1.z.boolean(),
    attributes: zod_1.z.array(catalog_js_1.CanonicalAttribute).default([]),
});
exports.CatalogItemImage = zod_1.z.object({
    /** SHA-256 of the bytes, lower-case hex. */
    contentHash: zod_1.z.string().regex(/^[0-9a-f]{64}$/),
    contentType: zod_1.z.string().min(1).max(64),
});
/**
 * FN-219: whether a discoffer (markdown coupon) of `percent` may be applied
 * to the item. A percent with no rule is not decided by the item. How the
 * terminal combines the rule with the category's `discofferAllowed` (FN-160),
 * and what a percent with no rule means, is pending an owner decision
 * (FN-219 Q1/Q2).
 */
exports.CatalogItemDiscofferRule = zod_1.z.object({
    percent: zod_1.z.number().int().min(1).max(100),
    allowed: zod_1.z.boolean(),
});
exports.CatalogBootstrapItem = zod_1.z.object({
    id: zod_1.z.string(),
    sku: zod_1.z.string(),
    name: zod_1.z.string(),
    posName: zod_1.z.string().min(1).max(255).optional(),
    categoryId: zod_1.z.string(),
    unitOfMeasure: zod_1.z.enum(['each', 'kg', 'g', 'l', 'ml']),
    taxCategory: zod_1.z.string(),
    ageRestriction: zod_1.z.number().int(),
    isVariableWeight: zod_1.z.boolean(),
    skipBaggingAllowed: zod_1.z.boolean(),
    /**
     * RL-439: the product carries excise stamps — the till asks for
     * `exciseStampCount` stamps per unit before the line can be sold, each
     * matching `ExciseStampNumber`. Defaulted rather than required so a
     * payload from a server that predates the field reads as "no stamp".
     */
    isMarked: zod_1.z.boolean().default(false),
    /** RL-439: stamps per ONE unit; meaningful only while `isMarked`. */
    exciseStampCount: zod_1.z.number().int().min(1).max(catalog_js_1.EXCISE_STAMP_COUNT_MAX).default(1),
    /**
     * FN-220: `required`, `none` or `optional` (the till prompts for the stamp
     * and a cashier may skip it). Always consistent with `isMarked`. Optional,
     * with no default: a payload from a server that predates the field carries
     * only `isMarked`, and the till reads that as `required` / `none`.
     */
    exciseStampPolicy: catalog_js_1.ExciseStampPolicy.optional(),
    /**
     * FN-223: `scan_or_manual` or `scan_only` (the till offers no manual entry
     * of the stamp). Optional, with no default: a server that predates the
     * field omits it, and the till keeps `scan_or_manual`.
     */
    exciseEntryMode: catalog_js_1.ExciseEntryMode.optional(),
    weightCheckMode: zod_1.z.enum(['presence', 'tolerance', 'disabled']),
    /**
     * The item's own weight tolerance, as a percentage, when an operator set one
     * (SSC-89). Absent means no override. SecureScale reads it as an item weight
     * fact; a terminal may ignore it.
     *
     * Additive and optional, and omitted rather than sent as null, so an older
     * serialiser stays valid and a till's feed does not grow a null per item.
     */
    weightTolerancePctOverride: terminal_params_js_1.PercentString.optional(),
    barcodes: zod_1.z.array(exports.CatalogBootstrapBarcode),
    /**
     * Arbitrary key/value pairs the retailer carries, repeated per value — an
     * item may hold many values under one key (RFL-350). `segment` is the
     * load-bearing one: re:form Loyalty builds product segments from it, and
     * Yuki's items average 9.2 segments each, up to 29.
     *
     * ADVISORY on this wire. The till neither needs these nor should act on
     * them; they exist so an integration reading `/sync/catalog` can see what a
     * product belongs to. This differs from `vipPriceMinor`, which the Yuki
     * terminal composition applies from `/sync/prices`.
     *
     * Defaulted rather than required so an older serialiser stays valid.
     */
    attributes: zod_1.z.array(catalog_js_1.CanonicalAttribute).default([]),
    /**
     * The item's picture, content-addressed (FN-85). A terminal fetches the
     * bytes once from `GET /sync/catalog/images/{contentHash}` and keeps them
     * on disk for as long as the hash stays; a changed picture is a new hash.
     * Absent when the item has no picture — additive, an older serialiser
     * stays valid.
     */
    image: exports.CatalogItemImage.optional(),
    /**
     * SSC-107: the base unit's master-data gross mass, in integer milligrams,
     * as the owning source declares it (GP `WeightBrutto`). A nominal
     * logistics value, not a measured weight.
     *
     * ADVISORY on this wire, like `attributes`: the till does not read it. It
     * exists for SecureScale's master-data reader on `/sync/catalog`. Absent
     * when unknown, so an absent key on an `item.create` means "no weight",
     * not "unchanged". Not emitted on `/sync/catalog/bootstrap`.
     */
    grossWeightMg: zod_1.z.number().int().positive().optional(),
    /** SSC-107: the base unit's net mass in milligrams (GP `WeightNetto`). */
    netWeightMg: zod_1.z.number().int().positive().optional(),
    /**
     * SSC-107: which source supplied the two weights: `gp-compat`, or (SSC-108)
     * `operator` when entered in BOS. Present exactly when at least one is.
     */
    weightSource: zod_1.z.string().min(1).optional(),
    /**
     * SSC-109: who chose `weightCheckMode`: `operator` (item API or admin
     * form) or `import` (a catalogue import that carried the column). Absent
     * when nobody did and `weightCheckMode` is only the default, so an absent
     * key on an `item.create` means "defaulted", not "unchanged".
     *
     * ADVISORY on this wire: the till does not read it. SecureScale maps only an
     * explicit mode to a weight policy. Not emitted on `/sync/catalog/bootstrap`.
     */
    weightCheckModeSource: zod_1.z.enum(['operator', 'import']).optional(),
    /**
     * FN-219: the item's discoffer rules, one per coupon percent, ordered by
     * percent. Emitted on `/sync/catalog` and `/sync/catalog/bootstrap` only
     * when the item has at least one rule — absent means "no item-level rule",
     * so an item without rules is byte-identical to an older serialiser's.
     */
    discofferRules: zod_1.z.array(exports.CatalogItemDiscofferRule).optional(),
});
exports.CatalogBootstrapCategory = zod_1.z.object({
    id: zod_1.z.string(),
    /** Retailer's source code (YUKI-57). Null for locally-authored categories. */
    externalCode: zod_1.z.string().min(1).max(50).nullable().optional(),
    /** Parent's retailer code when one exists; additive for older consumers. */
    parentExternalCode: zod_1.z.string().min(1).max(50).nullable().optional(),
    name: zod_1.z.string(),
    slug: zod_1.z.string(),
    parentId: zod_1.z.string().nullable(),
    sortOrder: zod_1.z.number().int(),
    /**
     * FN-160: whether a discoffer (markdown coupon) may be applied to items of
     * this category — null inherits from the parent, and a root that does not
     * decide means no. The terminal walks `parentId` to resolve it. Additive:
     * an older serialiser omits it, which reads as "inherit".
     */
    discofferAllowed: zod_1.z.boolean().nullable().optional(),
});
exports.CatalogBootstrap = zod_1.z.object({
    categories: zod_1.z.array(exports.CatalogBootstrapCategory),
    items: zod_1.z.array(exports.CatalogBootstrapItem),
    /**
     * Sprint 3 addition (H-77). Denormalised bound country pack
     * manifest — the Flutter side reads `fiscalAdapter.bindingId`
     * to select its adapter implementation, plus `currency`,
     * `mandatoryFiscalText`, `receiptTemplate` for rendering. Null
     * if the terminal has no binding or the bound pack is no longer
     * enabled+bundled.
     */
    countryPack: country_pack_js_1.CountryPackManifest.nullable().optional(),
    /**
     * Sprint 3 addition. Map of `taxCategory` → current `ratePercent`
     * for the calling terminal's bound country pack. Categories with
     * no active rate are OMITTED so the terminal hard-fails on sale of
     * items in those categories rather than silently zero-rating. See
     * apps/bos/docs/modules/tax-rates.md.
     */
    taxRates: tax_rates_js_1.TaxRatesBootstrap.optional(),
    /**
     * Sprint 3 addition. Resolved effective receipt configs for all
     * seven receipt types (sale / refund / void / parked / x_report /
     * z_report / copy), ready for the terminal renderer to consume
     * directly. See apps/bos/docs/modules/receipt-config.md.
     */
    receiptConfig: receipt_config_js_1.ReceiptConfigBootstrap.optional(),
    /**
     * Sprint 4.7 addition. Catalog hotkey tree snapshot — the
     * cashier-facing button panel on the left 45% of the POS screen.
     * Optional so older terminals (v1.0) still parse the envelope;
     * v1.1+ terminals read it. See apps/bos/docs/modules/catalog-hotkeys.md.
     */
    hotkeys: catalog_hotkeys_js_1.CatalogHotkeysBootstrap.optional(),
    snapshotAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=terminal.js.map